export type GoalCategory =
  | 'COGNITION'
  | 'LANGUAGE'
  | 'MOTOR'
  | 'SOCIAL'
  | 'EMOTIONAL'
  | 'SENSORY'
  | 'SELF_HELP'
  | 'BEHAVIOURAL';

/** Domains in therapy goal picker (AAC is filtered by tags/title, stored as LANGUAGE in DB). */
export type TherapyDomainFilter = GoalCategory | 'AAC' | 'ALL';

export type TherapyDomainCategory = Exclude<TherapyDomainFilter, 'ALL'>;

/** Display order for goal-library domain filters (includes categories that may have no library rows yet). */
export const THERAPY_DOMAIN_ORDER: TherapyDomainCategory[] = [
  'SOCIAL',
  'LANGUAGE',
  'COGNITION',
  'MOTOR',
  'SELF_HELP',
  'EMOTIONAL',
  'BEHAVIOURAL',
  'SENSORY',
  'AAC',
];

export const THERAPY_DOMAIN_LABELS: Record<TherapyDomainCategory, string> = {
  SOCIAL: 'Social',
  LANGUAGE: 'Language',
  COGNITION: 'Cognition',
  MOTOR: 'Motor',
  SELF_HELP: 'Self help',
  EMOTIONAL: 'Emotional',
  BEHAVIOURAL: 'Behavioural',
  SENSORY: 'Sensory',
  AAC: 'AAC',
};

export interface TherapistGoalTaskLibraryItem {
  task_id: string;
  title: string;
  description: string;
  context: string;
  strategy_type: string;
  criteria: string;
  mt_row: {
    type: 'TASK';
    category: string;
    externalId: string;
    parentId: string;
    strategies_source: string;
    tags: string[];
    status: 'NOT_STARTED';
    progressPercentage: number;
    isUserGenerated: boolean;
  };
}

export interface TherapistGoalLibraryItem {
  goal_id: string;
  skill_id: string;
  source_skill_name: string;
  source_skill_base: string;
  source_context_label: string;
  goal_title: string;
  goal_description: string;
  context: string;
  category: GoalCategory;
  domain: string;
  complexity: 'Basic' | 'Intermediate' | 'Advanced' | 'Expert';
  developmental_level: number;
  age_min_months: number;
  age_max_months: number;
  age_bands: string[];
  criteria: string;
  therapist_note: string;
  examples: string;
  search_text: string;
  prerequisite_skill_ids: string[];
  next_skill_ids: string[];
  prediction_trace?: {
    node_type: string;
    skill_id: string;
    goal_id: string;
    rollup_to_skill_id: string;
    next_skill_ids: string[];
  };
  mt_row: {
    type: 'MILESTONE';
    category: string;
    externalId: string;
    tags: string[];
    status: 'NOT_STARTED';
    progressPercentage: number;
    isUserGenerated: boolean;
  };
  tasks: TherapistGoalTaskLibraryItem[];
}

let cachedGoalLibrary: TherapistGoalLibraryItem[] | null = null;

const GOAL_LIBRARY_ENV_PATH = import.meta.env?.VITE_THERAPIST_GOAL_LIBRARY_PATH as string | undefined;

const GOAL_LIBRARY_PATHS = [
  ...(GOAL_LIBRARY_ENV_PATH ? [GOAL_LIBRARY_ENV_PATH] : []),
  '/data/tw_goal_blob_v1.b64?v=master-v4-993',
] as const;

function decodeGoalLibraryPayload(rawText: string): string {
  const trimmed = rawText.trim();
  if (!trimmed) return '';

  // Plain JSONL format
  if (trimmed.startsWith('{') || trimmed.includes('\n{')) {
    return rawText;
  }

  // Obfuscated base64 format
  try {
    const binary = atob(trimmed);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const decoded = new TextDecoder('utf-8').decode(bytes);
    if (decoded.includes('{') && decoded.includes('\n')) {
      return decoded;
    }
  } catch {
    // ignore and fall through to raw text
  }

  return rawText;
}

export async function loadTherapistGoalLibrary(): Promise<TherapistGoalLibraryItem[]> {
  if (cachedGoalLibrary) return cachedGoalLibrary;

  let rawText = '';
  for (const path of GOAL_LIBRARY_PATHS) {
    try {
      const response = await fetch(path);
      if (!response.ok) continue;
      const payloadText = await response.text();
      rawText = decodeGoalLibraryPayload(payloadText);
      if (rawText.trim()) break;
    } catch {
      // Try next path.
    }
  }

  if (!rawText.trim()) {
    throw new Error('Therapy goal library could not be loaded.');
  }

  const records: TherapistGoalLibraryItem[] = [];
  const lines = rawText.split('\n').map((line) => line.trim()).filter(Boolean);
  for (const line of lines) {
    try {
      records.push(JSON.parse(line) as TherapistGoalLibraryItem);
    } catch {
      // Skip malformed rows.
    }
  }

  cachedGoalLibrary = records;
  return records;
}

function ageBandDistance(goal: TherapistGoalLibraryItem, ageMonths: number): number {
  if (ageMonths < goal.age_min_months) return goal.age_min_months - ageMonths;
  if (ageMonths > goal.age_max_months) return ageMonths - goal.age_max_months;
  return 0;
}

/**
 * Age-eligible library pool for pickers.
 * Always keeps in-band goals, then fills thin categories with the nearest
 * library rows so Under-1 / Age 0 does not collapse to a single Self-help goal.
 */
export function getGoalsForAge(
  ageMonths: number,
  goals: TherapistGoalLibraryItem[],
): TherapistGoalLibraryItem[] {
  if (goals.length === 0) return [];

  const months = Number.isFinite(ageMonths) ? Math.max(0, ageMonths) : 0;
  const MIN_PER_CATEGORY = 8;

  const exactMatches = goals.filter(
    (goal) => goal.age_min_months <= months && months <= goal.age_max_months,
  );

  const merged: TherapistGoalLibraryItem[] = [];
  const seen = new Set<string>();
  for (const goal of exactMatches) {
    seen.add(goal.goal_id);
    merged.push(goal);
  }

  const categories = [...new Set(goals.map((goal) => goal.category))];
  for (const category of categories) {
    const have = merged.filter((goal) => goal.category === category).length;
    if (have >= MIN_PER_CATEGORY) continue;

    const extras = goals
      .filter((goal) => goal.category === category && !seen.has(goal.goal_id))
      .map((goal) => ({ goal, distance: ageBandDistance(goal, months) }))
      .sort(
        (a, b) =>
          a.distance - b.distance || a.goal.goal_title.localeCompare(b.goal.goal_title),
      )
      .slice(0, MIN_PER_CATEGORY - have)
      .map((entry) => entry.goal);

    for (const goal of extras) {
      seen.add(goal.goal_id);
      merged.push(goal);
    }
  }

  return merged;
}

/**
 * Returns up to `limit` unique goals whose `category` is in `categories`, sorted by title.
 * Used on the public parent hub to preview representative goals per developmental area.
 */
export function getTopGoalsForCategories(
  goals: TherapistGoalLibraryItem[],
  categories: GoalCategory[],
  limit = 10,
): TherapistGoalLibraryItem[] {
  if (categories.length === 0) return [];
  const catSet = new Set(categories);
  const matched = goals.filter((g) => catSet.has(g.category));
  const seen = new Set<string>();
  const deduped: TherapistGoalLibraryItem[] = [];
  for (const g of matched) {
    if (seen.has(g.goal_id)) continue;
    seen.add(g.goal_id);
    deduped.push(g);
  }
  deduped.sort((a, b) => a.goal_title.localeCompare(b.goal_title));
  return deduped.slice(0, limit);
}

/** Domains that have at least one goal in the given pool (age-eligible or full library). */
export function getDomainsWithGoalsInPool(
  goals: TherapistGoalLibraryItem[],
): TherapyDomainCategory[] {
  const domains: TherapyDomainCategory[] = [];
  for (const category of THERAPY_DOMAIN_ORDER) {
    if (goals.some((goal) => goalMatchesTherapyDomain(goal, category))) {
      domains.push(category);
    }
  }
  return domains;
}

function goalLibraryHaystack(goal: TherapistGoalLibraryItem): string {
  return [
    goal.goal_title,
    goal.goal_description,
    goal.search_text,
    goal.domain,
    goal.source_skill_name,
    goal.source_skill_base,
    ...(goal.mt_row?.tags ?? []),
  ]
    .join(' ')
    .toLowerCase();
}

/** Library rows tagged MOTOR/SENSORY but about daily living / adaptive skills. */
const SELF_HELP_LIBRARY_PATTERNS: RegExp[] = [
  /\b(dress|undress|put on|take off|fasten|unfasten)\b/,
  /\b(shirt|pants|shoes?|socks?|coat|jacket|hat|buttons?)\b/,
  /\b(toilet|potty|toileting|diaper)\b/,
  /\b(wash(es|ing)? hands?|hand washing|brush(es|ing)? teeth|hair brush|brushing)\b/,
  /\b(spoon|fork|feeding|meal|snack|drink from|cup)\b/,
  /\b(zipper|button|buckle|velcro)\b/,
  /\b(hygiene|bath|shower|groom)\b/,
  /\bself[- ]care\b/,
  /\badaptive\b/,
  /\btolerates\b.*\b(hair brush|toothbrush|nail)\b/,
];

function libraryGoalMatchesSelfHelp(goal: TherapistGoalLibraryItem): boolean {
  if (goal.category === 'SELF_HELP') return true;
  if (!['MOTOR', 'SENSORY'].includes(goal.category)) return false;
  const haystack = goalLibraryHaystack(goal);
  return SELF_HELP_LIBRARY_PATTERNS.some((pattern) => pattern.test(haystack));
}

/** LANGUAGE rows that describe sign, picture, device, or gestural communication. */
const AAC_LANGUAGE_PATTERNS: RegExp[] = [
  /\b(sign|picture|icon|device|symbol).{0,40}(communicat|request|answer|help|name|word)\b/,
  /\b(communicat|request|answer|help|name|word).{0,40}(sign|picture|icon|device|symbol)\b/,
  /\b(sign language|makaton|augmentative)\b/,
  /\bpoints? to (ask|request)\b/,
  /\bpoint(s|ing)? toward a desired\b/,
  /\bword approximation\b/,
  /\bcombines two words\/signs\/icons\b/,
  /\busing (speech, )?sign, picture, or device\b/,
  /\busing a word, sign, picture, or device\b/,
];

function libraryGoalMatchesAac(goal: TherapistGoalLibraryItem): boolean {
  const tags = (goal.mt_row?.tags ?? []).map((tag) => tag.toLowerCase());
  if (tags.some((tag) => tag.includes('aac') || tag === 'domain:aac')) return true;
  const haystack = goalLibraryHaystack(goal);
  if (haystack.includes('aac') || haystack.includes('pecs')) return true;
  if (goal.category !== 'LANGUAGE') return false;
  return AAC_LANGUAGE_PATTERNS.some((pattern) => pattern.test(haystack));
}

/** Sensory / emotional regulation goals that map to behavioural intervention. */
const BEHAVIOURAL_LIBRARY_PATTERNS: RegExp[] = [
  /\b(behavio|behaviour)\b/,
  /\b(tantrum|meltdown)\b/,
  /\b(aggress|hitting|kicking|biting|scratching)\b/,
  /\b(non[- ]compliance|defian|refus(es|al)?)\b/,
  /\b(self[- ]control|impulsiv|impulse control)\b/,
  /\b(calm down|de[- ]escalat)\b/,
  /\b(follow(s|ing)? (simple |basic )?instruction)\b/,
  /\b(transition|wait(ing)? (calmly|patiently|turn))\b/,
  /\b(routine|schedule)\b.*\b(follow|accept|tolerat)\b/,
  /\b(tolerat(e|es|ing)? (no|change|transition))\b/,
  /\b(accept(s|ing)? (no|limit|boundary|consequence))\b/,
  /\b(shar(es|ing) (toy|material|object))\b/,
  /\b(cooperat(e|es|ion|ing))\b/,
  /\b(stay(s|ing) seated|on task|attention to task)\b/,
  /\bwaits? with support without escalating\b/,
  /\bwaits? without (meltdown|escalat)\b/,
];

function libraryGoalMatchesBehavioural(goal: TherapistGoalLibraryItem): boolean {
  if (goal.category === 'BEHAVIOURAL') return true;
  if (!['SENSORY', 'EMOTIONAL', 'SOCIAL'].includes(goal.category)) return false;
  const haystack = goalLibraryHaystack(goal);
  return BEHAVIOURAL_LIBRARY_PATTERNS.some((pattern) => pattern.test(haystack));
}

export function goalMatchesTherapyDomain(
  goal: TherapistGoalLibraryItem,
  domain: TherapyDomainCategory,
): boolean {
  if (domain === 'AAC') {
    return libraryGoalMatchesAac(goal);
  }
  if (domain === 'SELF_HELP') {
    return libraryGoalMatchesSelfHelp(goal);
  }
  if (domain === 'BEHAVIOURAL') {
    return libraryGoalMatchesBehavioural(goal);
  }
  return goal.category === domain;
}

export function searchGoals(
  goals: TherapistGoalLibraryItem[],
  query: string,
  category?: TherapyDomainFilter,
): TherapistGoalLibraryItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  return goals.filter((goal) => {
    const matchesCategory =
      !category || category === 'ALL' || goalMatchesTherapyDomain(goal, category);
    if (!matchesCategory) return false;
    if (!normalizedQuery) return true;

    const searchable = [
      goal.goal_title,
      goal.goal_description,
      goal.criteria,
      goal.context,
      goal.search_text,
      goal.source_skill_name,
      goal.domain,
      goal.skill_id,
      goal.goal_id,
      ...(goal.mt_row?.tags ?? []),
    ]
      .join(' ')
      .toLowerCase();
    return searchable.includes(normalizedQuery);
  });
}
